import type { ReactNode } from 'react';
import { Link as AriaLink } from 'react-aria-components';
import { cn } from './lib/cn';

export type StatTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export type StatProps = {
  label: string;
  /** A number is grouped ("1,234"); a string is shown as it is; null or none is a dash. */
  value?: number | string | null;
  /** The dot before the label. */
  tone?: StatTone;
  /** A short line under the figure. */
  hint?: ReactNode;
  /** The figure opens the list it counts. */
  href?: string;
  isLoading?: boolean;
};

const NUMBER = new Intl.NumberFormat('en-US');

const DOTS: Record<StatTone, string> = {
  neutral: 'sk:bg-fg-tertiary',
  info: 'sk:bg-info',
  success: 'sk:bg-success-solid',
  warning: 'sk:bg-warning-solid',
  danger: 'sk:bg-danger-solid',
};

const BOX = 'sk:flex sk:min-w-0 sk:flex-col sk:gap-1 sk:px-4 sk:py-3';
const LINK = cn(
  'sk:text-fg sk:no-underline sk:outline-none sk:transition-colors sk:hover:bg-fill-quaternary',
  'sk:rac-focus-visible:outline-2 sk:rac-focus-visible:outline-solid sk:rac-focus-visible:-outline-offset-2 sk:rac-focus-visible:outline-primary',
);

/** One headline figure: a toned label, the number, an optional line under it. Sits in a `StatGroup`. */
export function Stat({
  label,
  value,
  tone = 'neutral',
  hint,
  href,
  isLoading = false,
}: StatProps) {
  const shown =
    value === null || value === undefined
      ? '—'
      : typeof value === 'number'
        ? NUMBER.format(value)
        : value;
  const body = (
    <>
      <span className="sk:flex sk:items-center sk:gap-2 sk:text-dense sk:font-medium sk:text-fg-secondary">
        <span
          aria-hidden
          data-testid="stat-dot"
          className={cn('sk:size-1.5 sk:shrink-0 sk:rounded-full', DOTS[tone])}
        />
        <span className="sk:truncate">{label}</span>
      </span>
      {isLoading ? (
        <span
          data-testid="stat-loading"
          className="sk:block sk:h-8 sk:w-16 sk:animate-pulse sk:rounded-option sk:bg-fill-secondary"
        />
      ) : (
        <span className="sk:text-2xl sk:font-semibold sk:tabular-nums sk:text-fg">
          {shown}
        </span>
      )}
      {hint ? (
        <span className="sk:text-xs sk:text-fg-tertiary">{hint}</span>
      ) : null}
    </>
  );
  const name = `${label}: ${isLoading ? 'loading' : shown}`;

  return href ? (
    <AriaLink
      href={href}
      aria-label={name}
      aria-busy={isLoading || undefined}
      className={cn(BOX, LINK)}
    >
      {body}
    </AriaLink>
  ) : (
    // biome-ignore lint/a11y/useSemanticElements: a fieldset brings borders and min-width
    <div
      role="group"
      aria-label={name}
      aria-busy={isLoading || undefined}
      className={BOX}
    >
      {body}
    </div>
  );
}

export type StatGroupProps = { children: ReactNode };

/** Stats side by side in equal columns, a rule between each. Place it in a `Card` with no body padding. */
export function StatGroup({ children }: StatGroupProps) {
  return (
    <div className="sk:grid sk:auto-cols-fr sk:grid-flow-col sk:divide-x sk:divide-rule">
      {children}
    </div>
  );
}
